import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { WASTE_CATEGORIES, getCategoryColor } from '../config/wasteCategories';
import api from '../config/api';
import { toast } from 'react-toastify';
import { FaCamera, FaSpinner, FaTimes, FaMapMarkerAlt, FaTrash, FaImages, FaStar } from 'react-icons/fa';
import './ReportWasteModal.css';

const MAX_PHOTOS = 2;

const ReportWasteModal = ({ isOpen, onClose, userLocation, onReportCreated }) => {
  const { currentUser } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [preciseLocation, setPreciseLocation] = useState(null);
  
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  
  const [locationMethod, setLocationMethod] = useState('');
  const [manualAddress, setManualAddress] = useState('');
  const [geocodingAddress, setGeocodingAddress] = useState(false);

  const [foodData, setFoodData] = useState({
    foodType: 'packaged',
    allergenDeclaration: false,
    allergenDetails: '',
    eatBy: '',
    collectionDeadline: '',
    foodDisclaimerAccepted: false
  });

  const [charityData, setCharityData] = useState({
    itemCondition: 0
  });
  
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setStep(1);
    setSelectedCategory(null);
    setSelectedSubCategory('');
    setPhotoFiles([]);
    setPhotoPreviews([]);
    setLocationMethod('gps');
    setManualAddress('');
    setPreciseLocation(null);
    setGettingLocation(true);
    setFoodData({
      foodType: 'packaged',
      allergenDeclaration: false,
      allergenDetails: '',
      eatBy: '',
      collectionDeadline: '',
      foodDisclaimerAccepted: false
    });
    setCharityData({ itemCondition: 0 });
    getPreciseLocation();
  };

  const handleLocationMethodChange = (method) => {
    setLocationMethod(method);
    setPreciseLocation(null);
    setManualAddress('');
    
    if (method === 'gps') {
      getPreciseLocation();
    }
  };

  const handleCameraClick = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handleGalleryClick = () => {
    if (galleryInputRef.current) {
      galleryInputRef.current.click();
    }
  };

  const geocodeAddress = async () => {
    if (!manualAddress.trim()) {
      toast.error('Please enter an address');
      return;
    }
    
    setGeocodingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(manualAddress)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        setPreciseLocation({
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          accuracy: 100,
          address: data[0].display_name
        });
        toast.success('Location found!');
      } else {
        toast.error('Could not find that address. Please try a different one.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      toast.error('Error finding address. Please try again.');
    } finally {
      setGeocodingAddress(false);
    }
  };

  const getPreciseLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setPreciseLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          setGettingLocation(false);
        },
        (error) => {
          console.error('Location error:', error);
          toast.error('Could not get your precise location. Please enable location services.');
          setGettingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
      setGettingLocation(false);
    }
  };

  const handleCategorySelect = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setSelectedSubCategory('');
    setStep(2);
  };

  const isFoodOrCharity = () => {
    return selectedCategory === 'food_donation' || selectedCategory === 'charity_items';
  };

  const handleSubCategorySelect = (subCategory) => {
    setSelectedSubCategory(subCategory);
    if (selectedCategory === 'food_donation' || selectedCategory === 'charity_items') {
      setStep(3);
    } else {
      setStep(4);
    }
  };

  const validateFoodCharityFields = () => {
    if (selectedCategory === 'food_donation') {
      if (foodData.foodType === 'home_cooked' && !foodData.allergenDeclaration) {
        toast.error('Allergen declaration is mandatory for home-cooked food');
        return false;
      }
      if (foodData.foodType === 'home_cooked' && foodData.allergenDeclaration && !foodData.allergenDetails.trim()) {
        toast.error('Please provide allergen details');
        return false;
      }
      if (!foodData.eatBy) {
        toast.error('Please provide an eat-by date/time');
        return false;
      }
      if (!foodData.collectionDeadline) {
        toast.error('Please provide a collection deadline');
        return false;
      }
      if (!foodData.foodDisclaimerAccepted) {
        toast.error('You must accept the food safety disclaimer');
        return false;
      }
    }
    if (selectedCategory === 'charity_items') {
      if (!charityData.itemCondition || charityData.itemCondition < 1) {
        toast.error('Please rate the item condition (1-5 stars)');
        return false;
      }
    }
    return true;
  };

  const handleFoodCharityNext = () => {
    if (validateFoodCharityFields()) {
      setStep(4);
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = MAX_PHOTOS - photoFiles.length;
    if (remainingSlots <= 0) {
      toast.warning(`Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }
    
    const filesToAdd = files.slice(0, remainingSlots);
    const newPreviews = filesToAdd.map(file => URL.createObjectURL(file));
    
    setPhotoFiles(prev => [...prev, ...filesToAdd]);
    setPhotoPreviews(prev => [...prev, ...newPreviews]);
    
    if (files.length > remainingSlots) {
      toast.info(`Only ${remainingSlots} more photo(s) allowed`);
    }
    
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const validateEvidence = () => {
    if (photoFiles.length === 0) {
      toast.error('Please upload at least one photo');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!preciseLocation) {
      toast.error('Could not get your location. Please try again.');
      getPreciseLocation();
      return;
    }

    if (!validateEvidence()) {
      return;
    }

    try {
      setLoading(true);

      const reportData = {
        category: selectedCategory,
        wasteType: selectedSubCategory,
        description: '',
        evidenceType: 'photo',
        location: {
          lat: preciseLocation.lat,
          lng: preciseLocation.lng,
          accuracy: preciseLocation.accuracy
        },
        reportedBy: currentUser?.displayName || currentUser?.email || 'Anonymous',
        reportedById: currentUser?.uid || 'anonymous'
      };

      if (selectedCategory === 'food_donation') {
        reportData.is_food_donation = true;
        reportData.food_type = foodData.foodType;
        reportData.allergen_declaration = foodData.allergenDeclaration;
        reportData.allergen_details = foodData.allergenDetails;
        reportData.eat_by = foodData.eatBy;
        reportData.collection_deadline = foodData.collectionDeadline;
        reportData.food_disclaimer_accepted = foodData.foodDisclaimerAccepted;
      }

      if (selectedCategory === 'charity_items') {
        reportData.is_charity_item = true;
        reportData.item_condition = charityData.itemCondition;
      }

      await api.createWasteReport(reportData, photoFiles[0]);

      toast.success('Waste report submitted successfully!');
      onReportCreated && onReportCreated();
      onClose();
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="report-modal-overlay" onClick={onClose}>
      <div className="report-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <FaTimes />
        </button>

        <div className="modal-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1</div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2</div>
          <div className="progress-line"></div>
          {isFoodOrCharity() && (
            <>
              <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>3</div>
              <div className="progress-line"></div>
            </>
          )}
          <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>{isFoodOrCharity() ? '4' : '3'}</div>
        </div>

        {step === 1 && (
          <div className="modal-step">
            <h2>Select Waste Category</h2>
            <p className="step-description">Choose the main category of the waste you found</p>
            <div className="category-grid">
              {Object.keys(WASTE_CATEGORIES).map((key) => (
                <button
                  key={key}
                  className="category-card"
                  style={{ borderColor: getCategoryColor(key) }}
                  onClick={() => handleCategorySelect(key)}
                >
                  <div className="category-icon" style={{ backgroundColor: getCategoryColor(key) }}>
                    {WASTE_CATEGORIES[key].icon}
                  </div>
                  <span className="category-name">{WASTE_CATEGORIES[key].name}</span>
                  <div className="category-color-dot" style={{ backgroundColor: getCategoryColor(key) }}></div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && selectedCategory && (
          <div className="modal-step">
            <button className="back-btn" onClick={() => setStep(1)}>Back</button>
            <h2>Select Waste Type</h2>
            <p className="step-description">
              <span className="selected-category" style={{ backgroundColor: getCategoryColor(selectedCategory) }}>
                {WASTE_CATEGORIES[selectedCategory].icon} {WASTE_CATEGORIES[selectedCategory].name}
              </span>
            </p>
            <div className="subcategory-list">
              {WASTE_CATEGORIES[selectedCategory].types.map((type, index) => (
                <button
                  key={index}
                  className={`subcategory-item ${selectedSubCategory === type ? 'selected' : ''}`}
                  onClick={() => handleSubCategorySelect(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && isFoodOrCharity() && (
          <div className="modal-step">
            <button className="back-btn" onClick={() => setStep(2)}>Back</button>
            <h2>{selectedCategory === 'food_donation' ? 'Food Donation Details' : 'Charity Item Details'}</h2>
            <p className="step-description">Please provide additional details</p>

            {selectedCategory === 'food_donation' && (
              <div className="modal-conditional-fields">
                <div className="modal-form-group">
                  <label>Food Type *</label>
                  <div className="toggle-group">
                    <button
                      type="button"
                      className={`toggle-btn ${foodData.foodType === 'packaged' ? 'active' : ''}`}
                      onClick={() => setFoodData(prev => ({ ...prev, foodType: 'packaged' }))}
                    >
                      Packaged
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${foodData.foodType === 'home_cooked' ? 'active' : ''}`}
                      onClick={() => setFoodData(prev => ({ ...prev, foodType: 'home_cooked' }))}
                    >
                      Home Cooked
                    </button>
                  </div>
                </div>

                {foodData.foodType === 'home_cooked' && (
                  <div className="modal-form-group">
                    <label className="modal-checkbox-label">
                      <input
                        type="checkbox"
                        checked={foodData.allergenDeclaration}
                        onChange={(e) => setFoodData(prev => ({ ...prev, allergenDeclaration: e.target.checked }))}
                      />
                      Allergen Declaration (mandatory) *
                    </label>
                    {foodData.allergenDeclaration && (
                      <textarea
                        placeholder="List all allergens present (e.g., nuts, gluten, dairy, eggs...)"
                        value={foodData.allergenDetails}
                        onChange={(e) => setFoodData(prev => ({ ...prev, allergenDetails: e.target.value }))}
                        rows="3"
                        className="modal-textarea"
                      />
                    )}
                  </div>
                )}

                <div className="modal-form-group">
                  <label>Eat By Date/Time *</label>
                  <input
                    type="datetime-local"
                    value={foodData.eatBy}
                    onChange={(e) => setFoodData(prev => ({ ...prev, eatBy: e.target.value }))}
                    className="modal-datetime-input"
                  />
                </div>

                <div className="modal-form-group">
                  <label>Collection Deadline *</label>
                  <input
                    type="datetime-local"
                    value={foodData.collectionDeadline}
                    onChange={(e) => setFoodData(prev => ({ ...prev, collectionDeadline: e.target.value }))}
                    className="modal-datetime-input"
                  />
                </div>

                <div className="modal-form-group">
                  <label className="modal-checkbox-label modal-disclaimer">
                    <input
                      type="checkbox"
                      checked={foodData.foodDisclaimerAccepted}
                      onChange={(e) => setFoodData(prev => ({ ...prev, foodDisclaimerAccepted: e.target.checked }))}
                    />
                    I confirm this food is safe for consumption and I accept responsibility for the accuracy of this listing. *
                  </label>
                </div>
              </div>
            )}

            {selectedCategory === 'charity_items' && (
              <div className="modal-conditional-fields">
                <div className="modal-form-group">
                  <label>Item Condition *</label>
                  <div className="star-rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star-btn ${star <= charityData.itemCondition ? 'active' : ''}`}
                        onClick={() => setCharityData(prev => ({ ...prev, itemCondition: star }))}
                      >
                        <FaStar />
                      </button>
                    ))}
                    <span className="star-label">
                      {charityData.itemCondition === 0 && 'Select rating'}
                      {charityData.itemCondition === 1 && 'Poor'}
                      {charityData.itemCondition === 2 && 'Fair'}
                      {charityData.itemCondition === 3 && 'Good'}
                      {charityData.itemCondition === 4 && 'Very Good'}
                      {charityData.itemCondition === 5 && 'Excellent'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <button className="next-btn" onClick={handleFoodCharityNext}>
              Continue to Evidence
            </button>
          </div>
        )}

        {step === 4 && (
          <div className="modal-step">
            <button className="back-btn" onClick={() => setStep(isFoodOrCharity() ? 3 : 2)}>Back</button>
            <h2>Photo Evidence</h2>
            <p className="step-description">Maximum {MAX_PHOTOS} photos allowed</p>
            
            <div className="location-section">
              <label className="location-label">
                <FaMapMarkerAlt /> Location of Waste
              </label>
              <select
                className="location-dropdown"
                value={locationMethod}
                onChange={(e) => handleLocationMethodChange(e.target.value)}
              >
                <option value="">---Select Location---</option>
                <option value="gps">Live/Current Location</option>
                <option value="manual">Postcode/Zipcode/Street</option>
              </select>
              
              {locationMethod === 'gps' && (
                <div className="location-status">
                  {gettingLocation ? (
                    <span><FaSpinner className="spinning" /> Getting your location...</span>
                  ) : preciseLocation ? (
                    <span className="location-success">
                      Location captured (accuracy: {preciseLocation.accuracy?.toFixed(0)}m)
                    </span>
                  ) : (
                    <button onClick={getPreciseLocation} className="retry-location-btn">
                      Retry Location
                    </button>
                  )}
                </div>
              )}
              
              {locationMethod === 'manual' && (
                <div className="manual-address-section">
                  <input
                    type="text"
                    className="address-input"
                    placeholder="Enter postcode, zipcode, or street address..."
                    value={manualAddress}
                    onChange={(e) => setManualAddress(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && geocodeAddress()}
                  />
                  <button
                    className="find-location-btn"
                    onClick={geocodeAddress}
                    disabled={geocodingAddress || !manualAddress.trim()}
                  >
                    {geocodingAddress ? (
                      <><FaSpinner className="spinning" /> Finding...</>
                    ) : (
                      'Find Location'
                    )}
                  </button>
                  {preciseLocation && preciseLocation.address && (
                    <p className="found-address">Found: {preciseLocation.address}</p>
                  )}
                </div>
              )}
            </div>

            <div className="evidence-upload-section">
              <div className="evidence-upload-item">
                <label>Photo Evidence</label>
                
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoChange}
                  ref={cameraInputRef}
                  style={{ display: 'none' }}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  ref={galleryInputRef}
                  style={{ display: 'none' }}
                />
                
                <div className="photo-buttons">
                  <button
                    type="button"
                    className="take-photo-btn"
                    onClick={handleCameraClick}
                    disabled={photoFiles.length >= MAX_PHOTOS}
                  >
                    <FaCamera /> Take Photo
                  </button>
                  <button
                    type="button"
                    className="upload-photo-btn"
                    onClick={handleGalleryClick}
                    disabled={photoFiles.length >= MAX_PHOTOS}
                  >
                    <FaImages /> Gallery
                  </button>
                </div>
                
                {photoPreviews.length > 0 && (
                  <div className="photo-previews-grid">
                    {photoPreviews.map((preview, index) => (
                      <div key={index} className="preview-container">
                        <img src={preview} alt={`Preview ${index + 1}`} />
                        <button 
                          className="remove-photo-btn"
                          onClick={() => removePhoto(index)}
                          type="button"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="photo-count">{photoFiles.length}/{MAX_PHOTOS} photos added</p>
              </div>
            </div>

            <button
              className="submit-btn"
              onClick={handleSubmit}
              disabled={loading || photoFiles.length === 0 || !preciseLocation}
            >
              {loading ? (
                <>
                  <FaSpinner className="spinning" /> Submitting...
                </>
              ) : (
                'Submit Waste Report'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportWasteModal;
